import { figmaPxToWidthPercent, figmaPxToHeightPercent } from '@ui/Report';

/** Figma frame "8" — 1920×1080 report page. */
const FIGMA_PAGE_WIDTH = 1920;
const FIGMA_PAGE_HEIGHT = 1080;

/** Height÷width — maps equal |Δtop%| to |Δleft%| for a 45° line on the page. */
export const PRODUCT_GROUPS_REPORT_PAGE_HEIGHT_TO_WIDTH =
  FIGMA_PAGE_HEIGHT / FIGMA_PAGE_WIDTH;

/** Figma Frame 835 — donut chart block (right side of the dashboard). */
const FIGMA_CHART_BLOCK = { x: 1163, y: 359, width: 658, height: 658 } as const;

/** Figma title node 4644:12432 — "Product groups" header above the donut. */
const FIGMA_TITLE = { x: 1228, y: 359 } as const;

/** Donut outer edge diameter — Figma Ellipse 53, slightly enlarged from 422px. */
const FIGMA_DONUT_OUTER_DIAMETER_PX = 450;

export const PRODUCT_GROUPS_DONUT_OUTER_DIAMETER_PERCENT =
  figmaPxToWidthPercent(FIGMA_DONUT_OUTER_DIAMETER_PX);

/** Donut left edge — horizontally centered on the page; legend table sits to the right. */
export const PRODUCT_GROUPS_DONUT_LEFT_PERCENT = 48;

/** Donut center X — left edge + half the outer diameter. */
export const PRODUCT_GROUPS_DONUT_CENTER_X_PERCENT =
  PRODUCT_GROUPS_DONUT_LEFT_PERCENT +
  PRODUCT_GROUPS_DONUT_OUTER_DIAMETER_PERCENT / 2;

export const PRODUCT_GROUPS_DONUT_CENTER_Y_PERCENT =
  ((FIGMA_CHART_BLOCK.y + FIGMA_CHART_BLOCK.height / 2) / FIGMA_PAGE_HEIGHT) *
  100;

/** Donut height — square outer diameter as % of page height. */
export const PRODUCT_GROUPS_DONUT_HEIGHT_PERCENT = figmaPxToHeightPercent(
  FIGMA_DONUT_OUTER_DIAMETER_PX,
);

/** Gap between donut right edge and the product-groups legend table. */
export const PRODUCT_GROUPS_LEGEND_GAP_PERCENT = figmaPxToWidthPercent(36);

/** Leader lines from donut slices to the legend table (matches SBEF elements bar). */
export const PRODUCT_GROUPS_LEGEND_LINE_THICKNESS = figmaPxToWidthPercent(1);
/** Minimum outward radial stub when the row y-line misses the slice ray. */
export const PRODUCT_GROUPS_LEGEND_LINE_RADIAL_STUB_PERCENT =
  figmaPxToWidthPercent(14);
/** Fillet radius at each leader-line bend (stub↔arc and arc↔legend run). */
export const PRODUCT_GROUPS_LEGEND_LINE_CORNER_RADIUS_PERCENT =
  figmaPxToWidthPercent(10);
/** Dot at the segment/slice end of a leader line. */
export const PRODUCT_GROUPS_LEGEND_LINE_ANCHOR_DOT_SIZE =
  figmaPxToWidthPercent(4);
/** Thin inner border drawn along each donut slice edge (viewBox units). */
export const PRODUCT_GROUPS_DONUT_SLICE_INNER_BORDER_COLOR =
  'rgba(0, 0, 0, 0.1)';
export const PRODUCT_GROUPS_DONUT_SLICE_INNER_BORDER_WIDTH_VB = 0.4;
/** Padding from legend table left edge to the category icon. */
export const PRODUCT_GROUPS_LEGEND_ICON_OFFSET_PERCENT =
  figmaPxToWidthPercent(8);
/** Gap between category icon(s) and the label text. */
export const PRODUCT_GROUPS_LEGEND_ICON_LABEL_GAP_PERCENT =
  figmaPxToWidthPercent(8);
/** Gap between leader-line end and the category icon. */
export const PRODUCT_GROUPS_LEGEND_LINE_ICON_GAP_PERCENT =
  figmaPxToWidthPercent(8);

/** Nudge the legend table further right from the donut. */
const PRODUCT_GROUPS_LEGEND_SHIFT_PERCENT = figmaPxToWidthPercent(15);

/** Product-groups legend table — right of the donut. */
export const PRODUCT_GROUPS_LEGEND_LEFT_PERCENT =
  PRODUCT_GROUPS_DONUT_LEFT_PERCENT +
  PRODUCT_GROUPS_DONUT_OUTER_DIAMETER_PERCENT +
  PRODUCT_GROUPS_LEGEND_GAP_PERCENT +
  PRODUCT_GROUPS_LEGEND_SHIFT_PERCENT;

/** "Product groups" section header — aligned with donut left edge. */
export const PRODUCT_GROUPS_TITLE_LEFT_PERCENT =
  PRODUCT_GROUPS_DONUT_LEFT_PERCENT;
export const PRODUCT_GROUPS_TITLE_TOP_PERCENT =
  (FIGMA_TITLE.y / FIGMA_PAGE_HEIGHT) * 100;

/** Figma Frame 828 — proposal total + offset summary above the product-groups block. */
const FIGMA_PROPOSAL_SUMMARY = { x: 1085, y: 213 } as const;
export const PRODUCT_GROUPS_PROPOSAL_SUMMARY_LEFT_PERCENT =
  (FIGMA_PROPOSAL_SUMMARY.x / FIGMA_PAGE_WIDTH) * 100;
export const PRODUCT_GROUPS_PROPOSAL_SUMMARY_TOP_PERCENT =
  (FIGMA_PROPOSAL_SUMMARY.y / FIGMA_PAGE_HEIGHT) * 100;

// ---------------------------------------------------------------------------
// Dashboard layout (left column + lifecycle column + product-groups column)
// ---------------------------------------------------------------------------

/** Page-level title block — top-left of the dashboard. */
const FIGMA_PAGE_TITLE = { x: 65, y: 60 } as const;
export const PAGE_TITLE_LEFT_PERCENT =
  (FIGMA_PAGE_TITLE.x / FIGMA_PAGE_WIDTH) * 100;
export const PAGE_TITLE_TOP_PERCENT =
  (FIGMA_PAGE_TITLE.y / FIGMA_PAGE_HEIGHT) * 100;

/** SBEF section block — left column. */
const FIGMA_SBEF_BLOCK = { x: 65, y: 359, width: 600, height: 658 } as const;
export const SBEF_BLOCK_LEFT_PERCENT =
  (FIGMA_SBEF_BLOCK.x / FIGMA_PAGE_WIDTH) * 100;
export const SBEF_BLOCK_TOP_PERCENT =
  (FIGMA_SBEF_BLOCK.y / FIGMA_PAGE_HEIGHT) * 100;
export const SBEF_BLOCK_WIDTH_PERCENT = figmaPxToWidthPercent(
  FIGMA_SBEF_BLOCK.width,
);

/** SBEF segmented bar chart (elements + leader-line legend) — page-relative box.
 * Left edge aligned with the page title block (Figma offsetLeft 118px). */
export const SBEF_BAR_CHART_LEFT_PERCENT = figmaPxToWidthPercent(118);
export const SBEF_BAR_CHART_WIDTH_PERCENT = 40;

/** Lifecycle section block — middle column. */
const FIGMA_LIFECYCLE_BLOCK = {
  x: 720,
  y: 359,
  width: 380,
  height: 658,
} as const;
export const LIFECYCLE_BLOCK_LEFT_PERCENT =
  (FIGMA_LIFECYCLE_BLOCK.x / FIGMA_PAGE_WIDTH) * 100;
export const LIFECYCLE_BLOCK_TOP_PERCENT =
  (FIGMA_LIFECYCLE_BLOCK.y / FIGMA_PAGE_HEIGHT) * 100;
export const LIFECYCLE_BLOCK_WIDTH_PERCENT = figmaPxToWidthPercent(
  FIGMA_LIFECYCLE_BLOCK.width,
);
/** Block height as % of page height — parent frame has a definite (aspect-ratio) height. */
export const LIFECYCLE_BLOCK_HEIGHT_PERCENT = figmaPxToHeightPercent(
  FIGMA_LIFECYCLE_BLOCK.height,
);

/** % of plot height between consecutive SBEF (Elements) legend row centers. */
export const SBEF_LEGEND_ROW_SPACING_PCT = 10;

/** Plot height inside the product-groups block — matches SBEF bar {@link SbefSegmentedBar}. */
export const PRODUCT_GROUPS_PLOT_HEIGHT_PERCENT =
  LIFECYCLE_BLOCK_HEIGHT_PERCENT * 0.82;

/**
 * % of the donut box height between consecutive product-category legend row
 * centers — derived from {@link SBEF_LEGEND_ROW_SPACING_PCT} so rows in both
 * legends sit the same physical distance apart, even though each spacing is
 * expressed as a % of a differently-sized box (the SBEF legend rows are % of
 * {@link PRODUCT_GROUPS_PLOT_HEIGHT_PERCENT} — the SBEF bar's 82% plot area
 * — the donut legend rows are % of {@link PRODUCT_GROUPS_DONUT_HEIGHT_PERCENT}).
 */
export const PRODUCT_GROUPS_LEGEND_ROW_SPACING_PCT =
  (SBEF_LEGEND_ROW_SPACING_PCT * PRODUCT_GROUPS_PLOT_HEIGHT_PERCENT) /
  PRODUCT_GROUPS_DONUT_HEIGHT_PERCENT;

/** Coverage donut next to section headers — scaling number (page-relative, via getScalingSize). */
export const COVERAGE_DONUT_DIAMETER = figmaPxToWidthPercent(72);
/** Gap between the label and the donut ring. */
export const COVERAGE_DONUT_LABEL_GAP = figmaPxToWidthPercent(16);
/** Nudge so the donut centers on the section-title row (EPD + coverage ratio). */
export const COVERAGE_DONUT_TOP_PERCENT =
  PRODUCT_GROUPS_TITLE_TOP_PERCENT - figmaPxToHeightPercent(24);

/**
 * Target diff chip on the lifecycle block (Figma proposals overview).
 * Values are scaling numbers — feed to `getScalingSize`, which picks the unit.
 * These are genuine sizes (height/radius/font/letter-spacing), not page
 * fractions, so they stay scaling units rather than `%`.
 */
export const REPORT_DIFF_CHIP = {
  height: figmaPxToWidthPercent(24),
  paddingX: figmaPxToWidthPercent(8),
  borderRadius: figmaPxToWidthPercent(4),
  fontSize: figmaPxToWidthPercent(12),
  letterSpacing: figmaPxToWidthPercent(0.24),
} as const;

/**
 * Donut slice gap/corner-radius/border, re-derived here (not imported — that
 * module already imports from this one) so the Elements bar list can match
 * them exactly. Mirrors {@link PRODUCT_GROUPS_DONUT_SLICE_GAP_DEG} /
 * {@link PRODUCT_GROUPS_DONUT_TARGET_CORNER_RADIUS_VB} /
 * {@link PRODUCT_GROUPS_DONUT_SLICE_INNER_BORDER_WIDTH_VB} in
 * product-groups-donut.helpers.ts: at the reference outer radius (50 vb),
 * the slice gap's arc length is exactly 3 vb units, and its corner radius is
 * half of that.
 */
const DONUT_VB_TO_WIDTH_PERCENT =
  PRODUCT_GROUPS_DONUT_OUTER_DIAMETER_PERCENT / 100;
const DONUT_SLICE_GAP_ARC_LENGTH_VB = 3;
const DONUT_SLICE_CORNER_RADIUS_VB = DONUT_SLICE_GAP_ARC_LENGTH_VB / 2;
/**
 * 2× {@link PRODUCT_GROUPS_DONUT_SLICE_INNER_BORDER_WIDTH_VB} — SVG stroke is centered on the
 * path (half renders outside the slice), so the raw value is doubled before the ÷2 in
 * {@link SBEF_BAR_LIST}.partBorderWidth.
 */
const DONUT_SLICE_BORDER_WIDTH_VB = 0.8;

/**
 * "Elements" horizontal bar list — one row per SBEF group, bar length ∝
 * baseline CO₂e vs the largest baseline group, label following directly
 * after the bar (no leader line). Each row layers a baseline bar (behind)
 * and a proposal bar (in front, same left edge) — styled like donut slices:
 * same corner radius, same inner border. The baseline bar only peeks out
 * past the proposal bar's right edge when the proposal is below baseline.
 */
export const SBEF_BAR_LIST = {
  /** Bar row height — scaling number (feed to `getScalingSize`). */
  barHeight: figmaPxToWidthPercent(46.4),
  /** Outer (right) corner radius on every part — same radius as a donut slice corner; the left edge sits flush against the axis. */
  partRadius: DONUT_SLICE_CORNER_RADIUS_VB * DONUT_VB_TO_WIDTH_PERCENT,
  /**
   * Inner border on every part — visually matches the donut slice border.
   * Halved vs the raw vb value: the donut's SVG `stroke` is centered on the
   * path (half its width renders outside the slice), while a CSS `border`
   * with `box-sizing: border-box` renders fully inside the box — so the same
   * nominal width would look twice as thick here.
   */
  partBorderWidth:
    (DONUT_SLICE_BORDER_WIDTH_VB / 2) * DONUT_VB_TO_WIDTH_PERCENT * 1.5,
  /** Vertical gap between bar rows — 32% more than the donut slice gap. */
  rowGap: DONUT_SLICE_GAP_ARC_LENGTH_VB * DONUT_VB_TO_WIDTH_PERCENT * 1.32,
  /** Gap between the bar's right edge and the label block. */
  labelGap: figmaPxToWidthPercent(20),
  /** Longest bar (largest baseline group) as % of the row's available width. */
  maxTrackWidthPercent: 78,
} as const;

/** Raise the Elements block's top edge (bottom edge stays put) for more breathing room. */
const SBEF_BAR_LIST_TOP_RAISE_PX = 80;
export const SBEF_BAR_LIST_TOP_PERCENT =
  LIFECYCLE_BLOCK_TOP_PERCENT -
  figmaPxToHeightPercent(SBEF_BAR_LIST_TOP_RAISE_PX);
export const SBEF_BAR_LIST_HEIGHT_PERCENT =
  LIFECYCLE_BLOCK_HEIGHT_PERCENT +
  figmaPxToHeightPercent(SBEF_BAR_LIST_TOP_RAISE_PX);

/**
 * Converts a `SBEF_BAR_LIST` size (width-relative scaling number — uniform
 * `cqw` scaling, see {@link REPORT_DIFF_CHIP}) to a % of the Elements plot
 * box height: first rescale by the page aspect ratio (width-% → height-%),
 * then by how much of the page height the plot box itself spans.
 */
const SBEF_BAR_LIST_SCALING_TO_PLOT_PERCENT =
  (FIGMA_PAGE_WIDTH / FIGMA_PAGE_HEIGHT) * (100 / SBEF_BAR_LIST_HEIGHT_PERCENT);

/** Bar height as % of the Elements plot box height. */
export const SBEF_BAR_LIST_BAR_HEIGHT_PLOT_PERCENT =
  SBEF_BAR_LIST.barHeight * SBEF_BAR_LIST_SCALING_TO_PLOT_PERCENT;

/** Row gap as % of the Elements plot box height. */
export const SBEF_BAR_LIST_ROW_GAP_PLOT_PERCENT =
  SBEF_BAR_LIST.rowGap * SBEF_BAR_LIST_SCALING_TO_PLOT_PERCENT;

/** Row center-to-center pitch (bar height + row gap) as % of the plot box height. */
export const SBEF_BAR_LIST_ROW_PITCH_PERCENT =
  SBEF_BAR_LIST_BAR_HEIGHT_PLOT_PERCENT + SBEF_BAR_LIST_ROW_GAP_PLOT_PERCENT;

/**
 * SBEF legend "offset vs baseline" badge (Figma "Offset box" component,
 * node 5070:14290) — arrow icon + value. Ratios are relative to the rendered
 * font size so border, radius, icon, and right padding scale proportionally.
 */
export const SBEF_OFFSET_BOX_RATIO = {
  paddingRight: 4 / 14,
  borderRadius: 2 / 14,
  borderWidth: 1 / 14,
  iconHeight: 16 / 14,
} as const;
